import React, { useMemo, useRef, useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { ShareRoomFragment } from "~/__generated__/graphql"
import { AppText } from "~/ui/app-text"
import { HostPill } from "./room-content-card/host-pill"
import { sortHostsWithDragRacePriority } from "../room-content-card"
import { cn } from "~/common/cn"
import pluralize from "pluralize"

const HostGrid = ({
  users,
}: {
  users: {
    id: string
    avatarUrl?: string | null
    displayOrUsername?: string | null
    verifiedAccount?: boolean | null
  }[]
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [canScrollDown, setCanScrollDown] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const update = () => {
      setCanScrollDown(el.scrollTop < el.scrollHeight - el.clientHeight - 1)
    }

    const timerId = setTimeout(update, 200)
    el.addEventListener("scroll", update, { passive: true })
    return () => {
      clearTimeout(timerId)
      el.removeEventListener("scroll", update)
    }
  }, [])

  if (users.length <= 3) {
    return (
      <div className="flex shrink-0 flex-wrap gap-2">
        {users.map((member) => (
          <HostPill key={member.id} member={member} variant="compact" />
        ))}
      </div>
    )
  }

  return (
    <div className="relative min-h-0 w-full flex-1">
      <div
        ref={containerRef}
        className="max-h-[50vh] overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="grid grid-cols-3 gap-2 pb-4">
          {users.map((member) => (
            <HostPill key={member.id} member={member} variant="compact" />
          ))}
        </div>
      </div>
      {canScrollDown && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-base-blue/50 to-transparent" />
      )}
    </div>
  )
}

interface RoomHeaderProps {
  roomData: ShareRoomFragment
  expanded?: boolean
  variant?: "compact" | "full"
  logoSlot?: React.ReactNode
  onToggleExpand?: () => void
}

export const RoomHeader = ({
  roomData,
  expanded = false,
  variant = "compact",
  logoSlot,
  onToggleExpand,
}: RoomHeaderProps) => {
  const [searchParams] = useSearchParams()
  const inviterUsername = searchParams.get("u")
  const hostsCount = roomData.activeRoomMembershipsCount
  const membersCount = roomData.activeRoomFollowsCount

  const statsText = useMemo(() => {
    const parts: string[] = []
    if (hostsCount && hostsCount > 0) {
      parts.push(`${hostsCount.toLocaleString()} ${pluralize("host", hostsCount)}`)
    }
    if (membersCount && membersCount > 0) {
      parts.push(`${membersCount.toLocaleString()} ${pluralize("member", membersCount)}`)
    }
    return parts.join(" · ")
  }, [hostsCount, membersCount])

  const sortedHosts = useMemo(
    () =>
      sortHostsWithDragRacePriority(roomData.roomMembershipsPreview, roomData.id, inviterUsername),
    [roomData.roomMembershipsPreview, roomData.id, inviterUsername]
  )

  const displayedUsers = useMemo(() => sortedHosts.map((m) => m.user).slice(0, 100), [sortedHosts])

  if (variant === "full") {
    return (
      <div className="flex min-h-0 w-full flex-1 flex-col gap-2 overflow-hidden">
        <div className="flex shrink-0 items-center gap-4">
          {roomData.photoUrl && (
            <img
              src={roomData.photoUrl}
              className="aspect-square w-[72px] shrink-0 rounded-[16px] border border-base-white/20 object-cover"
              alt={roomData.name ?? "Room"}
            />
          )}
          <div className="flex min-w-0 flex-col gap-1">
            <AppText variant="h2" className="text-[24px] leading-[28px] text-base-white">
              {roomData.name}
            </AppText>
            {statsText && (
              <AppText variant="body3-medium" className="text-base-white opacity-60">
                {statsText}
              </AppText>
            )}
          </div>
        </div>
        {roomData.description && (
          <AppText
            variant="body3-medium"
            className="line-clamp-2 shrink-0 text-base-white opacity-80"
          >
            {roomData.description}
          </AppText>
        )}
        {displayedUsers.length > 0 && <HostGrid users={displayedUsers} />}
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col py-1">
      <div
        className="flex cursor-pointer items-center justify-between px-4"
        onClick={(e) => {
          if (!expanded && onToggleExpand) {
            e.stopPropagation()
            onToggleExpand()
          }
        }}
      >
        <div className="flex min-w-0 items-center gap-3">
          {roomData.photoUrl && (
            <img
              src={roomData.photoUrl}
              className="h-12 w-12 shrink-0 rounded-xl border border-base-white/10 object-cover"
              alt={roomData.name ?? "Room"}
            />
          )}
          <div className="flex min-w-0 flex-col">
            <AppText variant="h3" className="truncate text-base-white">
              {roomData.name}
            </AppText>
            {statsText && (
              <AppText variant="body3-medium" className="text-base-white opacity-60">
                {statsText}
              </AppText>
            )}
          </div>
        </div>
        {logoSlot && <div className="shrink-0">{logoSlot}</div>}
      </div>

      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          expanded ? "max-h-[200px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="flex flex-col gap-2 py-2">
          {roomData.description && (
            <AppText
              variant="body3-medium"
              className="line-clamp-2 px-4 text-base-white opacity-80"
            >
              {roomData.description}
            </AppText>
          )}
          {displayedUsers.length > 0 && (
            <div className="flex cursor-grab select-none flex-row overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex shrink-0 flex-row gap-1.5 px-4">
                {displayedUsers.map((member) => (
                  <HostPill key={member.id} member={member} variant="compact" />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
